// @ts-strict-ignore
/*
 *
 *   ███████╗████████╗  █████ █╗  ██████╗
 *   ██╔════╝ ╚══██╔══╝ ██╔═══██╗ ██╔══██╗
 *   ███████╗    ██║    ██║   ██║ ██████╔╝
 *   ╚════██║    ██║    ██║   ██║ ██╔═══╝
 *   ███████║    ██║    ╚██████╔╝ ██║
 *   ╚══════╝    ╚═╝     ╚═════╝  ╚═╝
 *
 * If you are making edits to this file,
 * ASK IN #ask-aviary FIRST.
 *
 * This file basically just wraps the new AviaryProvider from @fullscript/aviary-web to
 * provide backwards compatibility with DS4 components.
 *
 * So, changes should be made in the aviary repo, not here.
 *
 * https://git.fullscript.io/developers/aviary/-/blob/development/packages/aviary-web/src/utils/AviaryProvider/AviaryProvider.tsx
 *
 */
import { AviaryProvider as NewAviaryProvider } from "@fullscript/aviary-web";

import { LegacySnackWrap } from "@aviary/components/LegacySnackbar/LegacySnackWrap/LegacySnackWrap";
import { Toaster } from "@aviary/components/Toast/Toaster/Toaster";
import { BreakpointsProvider } from "@shared/context/BreakpointsContext";
import { useShouldFrontendRefresh } from "@shared/data/client/links/getShouldFrontendRefreshLink/shouldFrontendRefresh.store";

import { AviaryGlobalStyles } from "../AviaryGlobalStyles";
import * as styles from "../materialTheme";

import { ThemeKeyboardShorcut } from "./ThemeKeyboardShortcut";

type AviaryProviderProps = Pick<
  Parameters<typeof NewAviaryProvider>[0],
  "controlledTheme" | "noNormalization" | "children"
>;

interface Props extends AviaryProviderProps {
  /**
   * Boolean to enable Emerson theme instead of Fullscript
   * Doesn't work with dark mode
   */
  isEmerson?: boolean;
  /**
   * Boolean whether React has been hydrated . This is mainly used for SSR
   */
  hydrated?: boolean;
}

const AviaryProvider = ({
  noNormalization,
  controlledTheme,
  isEmerson,
  hydrated,
  children,
}: Props) => {
  const shouldFrontendRefresh = useShouldFrontendRefresh();

  return (
    <BreakpointsProvider hydrated={hydrated}>
      <NewAviaryProvider
        shouldReloadDocument={shouldFrontendRefresh}
        brand={isEmerson ? "emerson" : "fullscript"}
        controlledTheme={controlledTheme}
        materialThemeFn={styles.materialAviaryTheme}
        noNormalization={noNormalization}
      >
        <AviaryGlobalStyles noNormalization={noNormalization} />
        <ThemeKeyboardShorcut {...{ isEmerson, controlledTheme }} />
        <LegacySnackWrap>
          <Toaster>
            <>{children}</>
          </Toaster>
        </LegacySnackWrap>
      </NewAviaryProvider>
    </BreakpointsProvider>
  );
};

export { AviaryProvider, type Props };
